import React from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill, SectionPill } from "../components/tag/Primitives";

const assets = [
  { n: "01", name: "SEO Content", desc: "Articles that rank in Google. Written once, found for years.", ex: "A metabolic health doctor writes 300 articles. Those articles rank. New patients find her." },
  { n: "02", name: "Free Tools", desc: "Calculators and AI assistants that capture leads passively.", ex: "A construction lender builds a free loan calculator. Leads captured automatically." },
  { n: "03", name: "Lead Magnets", desc: "Downloadable guides that build email lists.", ex: "An IEP advocate creates a free guide. The email list grows with the right audience." },
  { n: "04", name: "Email Newsletter", desc: "A direct owned channel. No algorithm. No platform risk.", ex: "An email list is yours. Email converts at 5-10x social for professional services." },
  { n: "05", name: "Digital Products", desc: "Guides and courses that earn without trading time.", ex: "A therapist's $47 workbook sells 100 copies/month. $56,400/year. Zero extra effort." },
  { n: "06", name: "AI Assistant", desc: "Expert knowledge available 24/7.", ex: "A financial advisor's AI qualifies leads, demonstrates expertise, never sleeps." },
  { n: "07", name: "Distribution System", desc: "One piece of content across every channel.", ex: "One article becomes a LinkedIn post, email, Instagram graphic, and Pinterest pin." },
];

const rows = [
  ["Invisible online", "Discoverable through search"],
  ["Referrals only", "Multiple digital channels"],
  ["No email list", "Email list owned and growing"],
  ["Income tied to hours", "Digital products earning"],
  ["Losing to influencers", "Assets compounding"],
  ["Random content", "Clear content strategy"],
];

const realLabs = [
  { name: "MetabolicLabOS", sector: "Healthcare", accent: "#3aab5a", built: "Built: 300+ articles, AI coach, subscriptions", result: "A complete authority ecosystem in health." },
  { name: "BeaconsIEP", sector: "Education", accent: "#5a8aeb", built: "Built: AI companion, 22 guides, bilingual", result: "A complete authority ecosystem in education." },
  { name: "ChalkOS", sector: "Sports", accent: "#eb5a5a", built: "Built: SaaS platform, 3 tiers, league management", result: "A complete authority ecosystem in sports." },
];

const Section = ({ children, style }) => (
  <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "44px 32px", ...style }}>
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>{children}</div>
  </section>
);

export default function AuthorityOS() {
  return (
    <Layout title="AuthorityOS | The Authority Gap" path="/authoriteos">
      {/* HERO */}
      <section style={{ background: "#090909", padding: "70px 32px 56px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 880, margin: "0 auto" }}>
          <div style={{ marginBottom: 24 }}><EyebrowPill>The Framework</EyebrowPill></div>
          <h1 className="tag-h1" style={{ marginBottom: 18 }}>
            <span>AuthorityOS</span><br />
            <span style={{ color: "#999" }}>The operating system for expert authority.</span>
          </h1>
          <p style={{ color: "#aaa", fontSize: 15 }}>
            Repeatable. Compounding. Proven across six industries.
          </p>
        </div>
      </section>

      {/* WHAT IT IS */}
      <Section>
        <div className="tag-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 44, alignItems: "start" }}>
          <div className="tag-body">
            <p>Most marketing companies sell tactics. SEO. Social media. Funnels. Ads. Each one is a channel. When you stop paying, it stops working.</p>
            <p>AuthorityOS is different. It builds assets. Assets that rank in Google, grow your email list, generate leads, and establish credibility — 24 hours a day, without your direct involvement.</p>
            <p>OS stands for Operating System. Just like your phone runs everything on top of it, AuthorityOS is the underlying system that makes all of your digital assets work together.</p>
          </div>
          <div style={{ background: "#111", borderLeft: "3px solid #FF6575", borderRadius: "0 8px 8px 0", padding: 20 }}>
            <p style={{ color: "#bbb", fontSize: 13, fontStyle: "italic", lineHeight: 1.7, margin: 0 }}>
              Most experts only have expertise. AuthorityOS builds the assets that make that expertise visible, trusted, and monetizable — without chasing attention every day.
            </p>
          </div>
        </div>
      </Section>

      {/* THE 7 ASSETS */}
      <Section>
        <h2 className="tag-h2" style={{ marginBottom: 24 }}>The 7 Authority Assets</h2>
        <div>
          {assets.map((a) => (
            <div key={a.n} data-testid={`asset-${a.n}`} style={{ padding: "22px 0", borderBottom: "1px solid #1a1a1a", display: "grid", gridTemplateColumns: "80px 1fr", gap: 24 }}>
              <div style={{ color: "#FF6575", fontSize: 40, fontWeight: 700, lineHeight: 1 }}>{a.n}</div>
              <div>
                <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>{a.name}</h3>
                <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.65, margin: "0 0 8px" }}>{a.desc}</p>
                <p style={{ color: "#888", fontSize: 12, fontStyle: "italic", margin: 0 }}>Example: {a.ex}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* BEFORE / AFTER */}
      <Section>
        <h2 className="tag-h2" style={{ marginBottom: 24 }}>The Before and After</h2>
        <div style={{ border: "1px solid #1a1a1a", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div style={{ background: "#1a0a0a", padding: "12px 18px", color: "#eb5a5a", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Without AuthorityOS</div>
            <div style={{ background: "#0a1a0a", padding: "12px 18px", color: "#3aab5a", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>With AuthorityOS</div>
          </div>
          {rows.map(([l, r], i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", background: i % 2 === 0 ? "#0d0d0d" : "#0f0f0f", borderTop: "1px solid #1a1a1a" }}>
              <div style={{ padding: "12px 18px", color: "#bbb", fontSize: 12 }}>{l}</div>
              <div style={{ padding: "12px 18px", color: "#bbb", fontSize: 12, borderLeft: "1px solid #1a1a1a" }}>{r}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* REAL WORLD */}
      <Section>
        <h2 className="tag-h2" style={{ marginBottom: 10 }}>AuthorityOS in the Real World</h2>
        <p style={{ color: "#aaa", fontSize: 13, fontStyle: "italic", marginBottom: 24 }}>Three examples. Same framework. Different niches. Same result.</p>
        <div className="tag-labs-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {realLabs.map((lab) => (
            <div key={lab.name} className="tag-lab-card">
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: lab.accent }} />
              <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 7 }}>{lab.sector}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: lab.accent, marginBottom: 12 }}>{lab.name}</div>
              <div style={{ height: 1, background: "#1a1a1a", marginBottom: 12 }} />
              <p style={{ color: "#bbb", fontSize: 12, lineHeight: 1.6, margin: "0 0 10px" }}>{lab.built}</p>
              <p style={{ color: "#FF6575", fontStyle: "italic", fontSize: 12, lineHeight: 1.55, margin: 0 }}>{lab.result}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <Link to="/portfolio" style={{ color: "#FF6575", fontSize: 13, fontWeight: 500 }}>See all six case studies →</Link>
        </div>
        <style>{`
          @media (max-width: 880px) { .tag-two-col { grid-template-columns: 1fr !important; gap: 28px !important; } }
          @media (max-width: 900px) { .tag-labs-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </Section>

      {/* CORAL CTA */}
      <section data-testid="cta-coral" style={{ background: "#FF6575", padding: "52px 32px", textAlign: "center" }}>
        <h2 style={{ color: "#fff", fontSize: 32, fontWeight: 700, margin: "0 0 12px" }}>What's your Authority Gap Score?</h2>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 15, margin: "0 auto 24px", maxWidth: 600 }}>
          Answer 7 questions. Get your score, your biggest gap, and your recommended next step. Free.
        </p>
        <Link
          to="/score"
          data-testid="cta-coral-score"
          style={{
            display: "inline-block",
            background: "#090909",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            padding: "12px 26px",
            borderRadius: 8,
          }}
        >
          Get Your Free Score
        </Link>
      </section>
    </Layout>
  );
}
